import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleById, getRelatedArticles } from '@/mocks/articleData';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <main>
          <div className="pt-16 md:pt-18 min-h-[60vh] flex flex-col items-center justify-center bg-background-50">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-background-200 rounded-full">
                <i className="ri-article-line text-3xl text-foreground-400"></i>
              </div>
              <h2 className="font-serif text-xl font-semibold text-foreground-900 mb-2">文章未找到</h2>
              <p className="text-foreground-500 mb-6">该文章可能已被删除或链接无效</p>
              <Link
                to="/articles"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回影视资讯
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const related = getRelatedArticles(article, 3);

  return (
    <>
      <Navbar />
      <main>
        <div className="pt-16 md:pt-18 bg-background-50">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '影视资讯', href: '/articles' },
              { label: article.title },
            ]}
          />

          <article className="py-6 md:py-10">
            <div className="container-custom">
              <div className="max-w-3xl mx-auto">
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <Link
                      to={`/category/${article.category}`}
                      className="text-xs px-2.5 py-1 bg-primary-100 text-primary-700 rounded-md hover:bg-primary-200 transition-colors"
                    >
                      {article.categoryName}
                    </Link>
                    {article.tags.map((tag) => (
                      <span key={tag} className="text-xs px-2.5 py-1 bg-background-200 text-foreground-500 rounded-md">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <h1 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight text-balance">
                    {article.title}
                  </h1>

                  <div className="flex items-center gap-4 text-sm text-foreground-400 flex-wrap">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {article.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {article.publishDate}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {article.readCount.toLocaleString()} 阅读
                    </span>
                  </div>
                </div>

                <div className="aspect-video rounded-xl overflow-hidden mb-8">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top"
                    title={`${article.title} - 日本在线观看 影视资讯`}
                  />
                </div>

                <div
                  className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                    [&_h2]:font-serif [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-4
                    [&_p]:mb-4 [&_p]:leading-relaxed
                    [&_strong]:text-foreground-900"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <script
                  type="application/ld+json"
                  dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                      '@context': 'https://schema.org',
                      '@type': 'Article',
                      headline: article.title,
                      description: article.excerpt,
                      image: article.image,
                      author: {
                        '@type': 'Person',
                        name: article.author,
                      },
                      datePublished: article.publishDate,
                      publisher: {
                        '@type': 'Organization',
                        name: '日本在线观看',
                      },
                    }),
                  }}
                />
              </div>

              {related.length > 0 && (
                <div className="mt-12 max-w-3xl mx-auto">
                  <h3 className="font-serif text-xl font-bold text-foreground-900 mb-6">
                    相关文章
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {related.map((r) => (
                      <Link
                        key={r.id}
                        to={`/article/${r.id}`}
                        className="group bg-white rounded-xl overflow-hidden hover:bg-background-50 transition-all duration-300"
                      >
                        <div className="aspect-video overflow-hidden">
                          <img
                            src={r.image}
                            alt={r.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                            title={`${r.title} - 日本在线观看`}
                          />
                        </div>
                        <div className="p-3">
                          <h4 className="font-medium text-foreground-900 text-sm line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {r.title}
                          </h4>
                          <p className="text-xs text-foreground-400 mt-1">{r.publishDate}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}